import React, { useState, FC, MouseEvent } from "react";

import { QueryObserverResult } from "@tanstack/react-query";
import { toast } from "react-toastify";

import {
  useApisMembersKaizenIdeasKaizenIdeaRepliesCreate,
  useEditorState,
  useEditorStateLinkContentGetter,
} from "~/hooks";

import {
  KaizenIdeaReplyDetailWithEditForm,
  KaizenIdeaDetail,
  TextEditor,
} from "~/components/organisms";

import {
  KaizenIdeaType,
  KaizenIdeaReplyType,
  MentionSuggestEmployeeType,
  PreviewWithFileType,
  ApiMemberKaizenIdeasKaizenIdeaRepliesIndexType,
} from "~/domains";

type PropsType = {
  highlightKaizenIdeaReplyId?: string;
  kaizenIdea: KaizenIdeaType;
  kaizenIdeaReplies: KaizenIdeaReplyType[];
  mentions: MentionSuggestEmployeeType[];
  className?: string;
  refetch: () => Promise<
    QueryObserverResult<ApiMemberKaizenIdeasKaizenIdeaRepliesIndexType, unknown>
  >;
};

export const KaizenIdeaRepliesWithKaizenIdea: FC<PropsType> = ({
  highlightKaizenIdeaReplyId = "",
  kaizenIdea,
  kaizenIdeaReplies,
  mentions,
  className = "",
  refetch,
}: PropsType) => {
  const [files, setFiles] = useState<PreviewWithFileType[]>([]);
  const [
    {
      value: editorState,
      onChange: setEditorState,
      jsonContent,
      hasText,
      setPlainText,
    },
  ] = useEditorState();
  const { fetchedData } = useEditorStateLinkContentGetter({
    watchEditorState: editorState,
    isWatch: true,
  });

  const { mutate, isLoading } =
    useApisMembersKaizenIdeasKaizenIdeaRepliesCreate();

  const handleSubmit = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    if (!hasText) {
      toast.error("テキストが空です");
      return;
    }
    const body = {
      content: jsonContent,
      attachFiles: files,
      quotePostId: fetchedData?.id,
    };

    mutate(
      {
        kaizenIdeaId: kaizenIdea.id,
        body,
      },
      {
        onSuccess: async () => {
          setPlainText("");
          setFiles([]);
          await refetch();
        },
      },
    );
  };

  return (
    <div className={className}>
      <div className="space-y-6">
        <KaizenIdeaDetail
          kaizenIdea={kaizenIdea}
          menuItems={[]}
          openReply={() => refetch()}
        />
        <div>
          {kaizenIdeaReplies.map((reply) => (
            <KaizenIdeaReplyDetailWithEditForm
              key={reply.id}
              kaizenIdeaReply={reply}
              mentions={mentions}
              refetch={refetch}
              isHighlight={reply.id === highlightKaizenIdeaReplyId}
            />
          ))}
        </div>
        <TextEditor
          submitButton={{
            isSubmitting: isLoading,
            onSubmit: handleSubmit,
          }}
          mentions={mentions}
          editorState={editorState}
          setEditorState={setEditorState}
          files={files}
          onChangeFiles={setFiles}
          blockQuote={fetchedData}
          isEmojiPickerTop
        />
      </div>
    </div>
  );
};
